import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/original/gallery1.jpg";
import heroPicture from "@/assets/original/gallery1.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
// Page-specific share image: a real marine canvas boat cover photo so the
// social preview reflects this page's subject instead of the generic gallery.
import shareImage from "@/assets/marine-canvas-boat.jpg";
import huntingTruckHighSeats from "@/assets/custom-hunting-truck-orange-tarp-high-seats-dog-box-seguin.jpg";


export const Route = createFileRoute("/covers-tops")({
  head: (ctx) => ({
      links: [canonicalLink(pathFromMatch(ctx))],
      meta: routeMeta(ctx, {
        title: "Boat Covers, Bimini Tops & Marine Canvas | Texas Made Shade",
        description: "Custom-fit boat covers, bimini tops, T-tops, and marine canvas patterned to your boat. Serving the Texas Gulf Coast — Corpus Christi, Rockport, Port Aransas.",
        image: shareImage,
        imageAlt: "Custom marine canvas boat cover by Texas Made Shade",
        socialTitle: "Covers & Tops — Boats, Marine Canvas | Texas Made Shade",
        socialDescription: "Custom-fit covers and bimini tops built for the Texas Gulf.",
      }),
    scripts: serviceHeadScripts({
      path: "/covers-tops",
      serviceName: "Covers & Tops",
      breadcrumbName: "Covers & Tops",
      description: "Custom marine canvas, boat covers, bimini tops, RV covers and equipment covers fabricated by Texas Made Shade.",
    }),
    }),
  component: () => (
    <ServicePage
      currentPath="/covers-tops"
      serviceName="Covers & Tops"
      eyebrow="★ COVERS & TOPS ★"
      title="Custom Boat Covers, Bimini Tops & Marine Canvas"
      intro="Mooring covers, T-top canvas, bimini tops, and full enclosures — patterned to your exact boat at the dock or in our Seguin shop."
      heroImage={heroPicture}
      features={[
        "Patterned on your boat for a perfect fit",
        "Sunbrella Marine fabric — UV and mildew resistant",
        "Stainless steel snaps and YKK marine zippers",
        "Hurricane-rated tie-down systems available",
        "T-tops, bimini tops, console covers, full enclosures",
        "Repair and re-canvas service for existing covers",
      ]}
      body={[
        { heading: "Mooring & Storage Covers", text: "Tight, water-shedding covers that protect your investment year-round on the Gulf or at the lake." },
        { heading: "Bimini & T-Top Canvas", text: "Replace faded or torn tops with marine-grade Sunbrella in any color." },
        { heading: "Custom Enclosures", text: "Full vinyl & canvas enclosures for cruisers and offshore boats. Built for serious weather." },
        { heading: "Commercial Covers & Banner Awnings", text: "Beyond marine canvas, we fabricate heavy-duty covers and storefront banner awnings for Texas businesses — same Sunbrella fabric, same welded-frame quality." },
      ]}
      faqs={[
        {
                "q": "Do you build custom boat covers and bimini tops?",
                "a": "Yes — boat covers, bimini tops, console covers, T-top canvas and storage covers are a core part of our shop. We pattern at the dock or in our Seguin facility using Sunbrella® Marine canvas, stainless snaps and YKK marine zippers."
        },
        {
                "q": "Can you re-cover my existing bimini frame?",
                "a": "Yes — bimini and T-top re-covers are common for us. Bring the boat to Seguin or send measurements and frame photos and we'll fabricate new canvas to your existing tubing."
        },
        {
                "q": "What fabric do you use for marine covers?",
                "a": "Sunbrella® Marine for full mooring and storage covers, Sunbrella® Plus or Top Gun for bimini tops and T-top canvas, and 30-gauge clear vinyl for windows. All UV-stable, mildew-resistant and built for Texas Gulf Coast humidity."
        },
        {
                "q": "How long does a custom marine cover take?",
                "a": "Standard turnaround is 2–4 weeks from measurement to install. Rush jobs available for fishing tournament season — call +1 (830) 390-9588 to discuss timing."
        }
]}
      applications={["Center Console", "Bay Boats", "Offshore Cruisers", "Pontoons", "Sailboats", "Personal Watercraft"]}
    >
      <section className="border-t-4 border-navy-deep bg-cream py-16">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <figure className="sm:float-right sm:ml-6 sm:mb-3 sm:w-[46%] md:w-[42%] lg:w-[38%] overflow-hidden border-4 border-navy-deep shadow-card">
            <img
              src={huntingTruckHighSeats}
              alt="Custom hunting truck flatbed rig with welded aluminum high-seat tower, diamond plate toolboxes and orange '7P' canvas identification panel by Texas Made Shade in Seguin, TX"
              width={1200}
              height={1600}
              loading="lazy"
              decoding="async"
              className="block h-auto w-full object-cover"
            />
            <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
              Custom hunting truck rig — high seats, dog box &amp; canvas ID panel
            </figcaption>
          </figure>
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Featured Build</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Custom Hunting Truck Covers, Welded High Seats &amp; Canvas ID Panels
          </h2>
          <p className="mt-4 text-lg text-navy-deep/80">
            Beyond marine canvas, our Seguin shop builds full custom hunting trucks — welded
            aluminum high-seat towers, diamond plate toolboxes, custom dog boxes and heavy
            canvas identification panels like the bright orange &ldquo;7P&rdquo; tarp shown here.
            Every panel is patterned to the exact frame, double-stitched in marine-grade
            Sunbrella® and finished with brass grommets so it stays drum-tight at highway speed.
          </p>

          <h3 className="mt-8 font-display text-xl font-bold text-navy-deep md:text-2xl">
            Welded Hunting Truck Rigs Built Start-to-Finish in Seguin, Texas
          </h3>
          <p className="mt-3 text-lg text-navy-deep/80">
            Pair a welded hunting truck rig with marine-vinyl seat re-covers, fitted bed wrap
            tarps and matching canvas gear bags and you&apos;ve got a complete South Texas hunting
            truck — fabrication, upholstery and canvas all under one roof. We build for ranch
            outfitters, quail guides and dog handlers running the brush country, the Hill Country
            and the Coastal Bend.
          </p>

          <h3 className="mt-8 font-display text-xl font-bold text-navy-deep md:text-2xl">
            What We Build Onto Custom Hunting Trucks
          </h3>
          <ul className="mt-3 space-y-2 text-base text-navy-deep/80">
            <li>★ Welded aluminum high-seat towers &amp; safety rails</li>
            <li>★ Custom dog boxes with vented kennel doors &amp; gear racks</li>
            <li>★ Diamond plate toolboxes, gun boxes &amp; flatbed builds</li>
            <li>★ Heavy canvas wrap tarps &amp; orange ID panels with brass grommets</li>
            <li>★ Marine-vinyl seat re-covers, headliners &amp; console wraps</li>
          </ul>
          <div className="clear-both" />
        </div>
      </section>
    </ServicePage>
  ),
});
